import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import DetailFrame from '../shared/Frames/DetailFrame';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { buildTabPath, resolveTabSlug } from '../shared/surface/tabPath';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import { getStatusColor } from '../../utils/functions';
import { fetchCaseOrientation, fetchLeadDetails, submitOffer } from '../../store/actions/Pipeline/PipelineActions';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { PiBriefcaseLight, PiCurrencyDollarLight, PiClockLight, PiCalendarLight, PiTagLight, PiMoneyLight, PiMapPinLight, PiFileTextLight, PiHandshakeLight, PiChatCircleTextLight } from 'react-icons/pi';
import DynamicEditor from '../Cases/Editor/DynamicEditor';
import GlassCard from '../shared/brand/GlassCard';
import { useDebounce } from 'use-debounce';
import EngagementTerms from '../Clients/EngagementLetter';
import BillingPage from './Billings/BillingPage';

export interface OfferPayload {
  totalPrice: number;
  plannedHours?: number;
  pricePerHour?: number;
  timeframe: string;
  expectedOutcome: string;
  paymentTerms: {
    retainer?: number;
    intervals?: string;
    methods: string[];
  };
  scopeOfWork: string;
  expertise?: string;
  communicationPlan?: string;
  risks?: string;
}

const LEAD_TABS = ['details', 'orientation', 'offer', 'engagement', 'billing'] as const;
type LeadTab = typeof LEAD_TABS[number];

const LeadDetailsPage: React.FC = () => {
  const { leadId, tab: tabParam } = useParams<{ leadId: string; tab?: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const lead = useSelector((state: RootState) => state.pipeline.leadDetails);
  const caseOrientation = useSelector((state: RootState) => state.pipeline.caseOrientation);
  const [offer, setOffer] = useState<OfferPayload>({
    totalPrice: 0,
    plannedHours: undefined,
    pricePerHour: undefined,
    timeframe: '',
    expectedOutcome: '',
    paymentTerms: { methods: ['PONS'] },
    scopeOfWork: '',
    expertise: '',
    communicationPlan: '',
    risks: '',
  });
  const [isEditingOffer, setIsEditingOffer] = useState(false);
  /* The active tab is a path segment (`/pipeline/:leadId/:tab?`): refresh,
     deep link and back/forward all resolve here. Unknown/absent → first tab. */
  const activeTab = resolveTabSlug(LEAD_TABS, tabParam) as LeadTab;
  const goToTab = useCallback(
    (value: string) => history.push(buildTabPath(`/pipeline/${leadId}`, value)),
    [history, leadId]
  );
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const { setBreadcrumbs } = useBreadcrumbs();
  const orientationEditorRef = useRef<HTMLDivElement | null>(null);

  const [localTotalPrice, setLocalTotalPrice] = useState<number>(offer.totalPrice);
  const [localPlannedHours, setLocalPlannedHours] = useState<number | undefined>(offer.plannedHours);
  const [localPricePerHour, setLocalPricePerHour] = useState<number | undefined>(offer.pricePerHour);

  const debouncedTotalPrice = useDebounce(localTotalPrice, 300)[0];
  const debouncedPlannedHours = useDebounce(localPlannedHours, 300)[0];
  const debouncedPricePerHour = useDebounce(localPricePerHour, 300)[0];

  useEffect(() => {
    dispatch(fetchLeadDetails(leadId));
  }, [dispatch, leadId]);

  useEffect(() => {
    if (lead && lead.offer) {
      setOffer(lead.offer);
      setLocalTotalPrice(lead.offer.totalPrice);
      setLocalPlannedHours(lead.offer.plannedHours);
      setLocalPricePerHour(lead.offer.pricePerHour);
    }
  }, [lead]);

  useEffect(() => {
    if (activeTab === 'orientation' && lead?.case_id && !caseOrientation) {
      dispatch(fetchCaseOrientation(lead.case_id));
    }
  }, [activeTab, lead, caseOrientation, dispatch]);

  useEffect(() => {
    let newTotalPrice = debouncedTotalPrice;
    let newPlannedHours = debouncedPlannedHours;
    let newPricePerHour = debouncedPricePerHour;

    if (newPlannedHours !== undefined && newPricePerHour !== undefined) {
      newTotalPrice = newPlannedHours * newPricePerHour;
      setLocalTotalPrice(newTotalPrice);
    } else if (newTotalPrice !== undefined && newPlannedHours !== undefined && newPlannedHours > 0) {
      newPricePerHour = newTotalPrice / newPlannedHours;
      setLocalPricePerHour(newPricePerHour);
    }

    setOffer((prev) => ({
      ...prev,
      totalPrice: newTotalPrice,
      plannedHours: newPlannedHours,
      pricePerHour: newPricePerHour,
    }));
  }, [debouncedTotalPrice, debouncedPlannedHours, debouncedPricePerHour]);

  const handleSubmitOffer = () => {
    if (leadId) {
      if (!offer.totalPrice || !offer.timeframe || !offer.expectedOutcome || !offer.scopeOfWork || offer.paymentTerms.methods.length === 0) {
        alert(t('Please fill all required fields'));
        return;
      }
      dispatch(submitOffer({ leadId, offer }));
      if (!isEditingOffer) {
        setOffer({
          totalPrice: 0,
          plannedHours: undefined,
          pricePerHour: undefined,
          timeframe: '',
          expectedOutcome: '',
          paymentTerms: { methods: ['PONS'] },
          scopeOfWork: '',
          expertise: '',
          communicationPlan: '',
          risks: '',
        });
        setLocalTotalPrice(0);
        setLocalPlannedHours(undefined);
        setLocalPricePerHour(undefined);
      }
      setIsEditingOffer(false);
    }
  };

  const handleEditOffer = () => {
    setIsEditingOffer(true);
  };

  const handleCancelEdit = () => {
    setIsEditingOffer(false);
    if (lead.offer) {
      setOffer(lead.offer);
      setLocalTotalPrice(lead.offer.totalPrice);
      setLocalPlannedHours(lead.offer.plannedHours);
      setLocalPricePerHour(lead.offer.pricePerHour);
    }
  };

  const tabLabels: Record<LeadTab, string> = {
    details: t('Details'),
    orientation: t('Case orientation'),
    offer: t('Offer'),
    engagement: t('Engagement terms'),
    billing: t('Billing'),
  };

  const breadcrumbs = [
    { name: t('Pipeline'), href: '/pipeline', current: false },
    { name: lead?.case_name || t('Lead'), href: `/pipeline/${leadId}`, current: false },
    { name: tabLabels[activeTab], href: `/pipeline/${leadId}/${activeTab}`, current: true },
  ];

  // Keep the top-bar breadcrumb context in step with the DetailFrame crumb.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, leadId, activeTab, setBreadcrumbs, t]);

  if (!lead) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="text-center text-gray-500 py-10">{t('Loading')}...</div>
      </DetailFrame>
    );
  }

  const tabs = [
    { label: t('Details'), value: 'details', icon: <PiFileTextLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Case orientation'), value: 'orientation', icon: <PiBriefcaseLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Offer'), value: 'offer', icon: <PiCurrencyDollarLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Engagement terms'), value: 'engagement', icon: <PiHandshakeLight className="h-5 w-5 text-gray-500" /> },
    { label: t('Billing'), value: 'billing', icon: <PiCurrencyDollarLight className="h-5 w-5 text-gray-500" /> },
  ];

  const renderDetailsTab = () => (
    <div className="gap-6 flex-1 flex">
      <GlassCard icon={<PiFileTextLight />} title={t('Case description')} className="flex-1">
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{lead.case_description || t('No description provided.')}</p>
      </GlassCard>
      <GlassCard icon={<PiChatCircleTextLight />} title={t('Client message')} className="flex-1">
        <p className="text-gray-600 dark:text-gray-400 italic">{lead.message || t('No message provided.')}</p>
      </GlassCard>
    </div>
  );

  const renderOrientationTab = () => (
    <div className="space-y-4">
      {caseOrientation ? (
        <DynamicEditor
          editorRef={orientationEditorRef}
          contentType="Case Orientation"
          matterId={lead.case_id}
          content={caseOrientation}
          onSave={() => {}}
          isFullscreen={isEditorFullscreen}
          setIsFullscreen={setIsEditorFullscreen}
          isReadOnly={true}
          fromlead={true}
        />
      ) : (
        <p className="text-gray-500 dark:text-gray-400">{t('Click the button to fetch case orientation.')}</p>
      )}
    </div>
  );

  const renderOfferTab = () => {
    if (lead.status === 'Accepted' && lead.offer) {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-success-100 dark:bg-success-900 rounded-lg">
            <p className="text-success-800 dark:text-success-200">{t('The client has accepted your offer. Please proceed to generate the engagement terms.')}</p>
          </div>
          <OfferCard offer={lead.offer} status={lead.status} />
        </div>
      );
    } else if (lead.status === 'Declined' && lead.offer) {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-danger-100 dark:bg-danger-900 rounded-lg">
            <p className="text-danger-800 dark:text-danger-200">{t('The client has declined your offer.')}</p>
            {lead.decline_reason && (
              <p className="mt-2"><strong>{t('Decline reason')}:</strong> {lead.decline_reason}</p>
            )}
            <div className="mt-4">
              <Button onClick={handleEditOffer} variant="primary" size="small">
                {t('Modify and resubmit offer')}
              </Button>
            </div>
          </div>
          <OfferCard offer={lead.offer} status={lead.status} />
          {isEditingOffer && (
            <div className="mt-4">
              <OfferForm offer={offer} setOffer={setOffer} onSubmit={handleSubmitOffer} onCancel={handleCancelEdit} />
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-medium mb-6 text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <PiCurrencyDollarLight /> {t('Submit offer')}
          </h2>
          <OfferForm offer={offer} setOffer={setOffer} onSubmit={handleSubmitOffer} />
        </div>
      );
    }
  };

  const renderEngagementTermsTab = () => (
    <EngagementTerms clientId={lead.client_id} />
  );

  return (
    <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
      <div className="py-6 mx-auto w-full overflow-y-auto">
        <div className="mb-6 px-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              {lead.case_name || t('Unnamed case')} - <span>{lead.client_name || 'N/A'}</span>
            </h2>
            <Badge color={getStatusColor(lead.status) as BadgeColor} className="text-sm">{lead.status}</Badge>
          </div>
          <div className="flex flex-wrap gap-4 text-gray-700 dark:text-gray-300">
            <Badge tooltip={t('Case type')}>
              <PiTagLight className="text-gray-500 dark:text-gray-400" />
              <span>{lead.case_type || 'N/A'}</span>
            </Badge>
            <Badge tooltip={t('Location')}>
              <PiMapPinLight className="text-gray-500 dark:text-gray-400" />
              <span>{lead.case_location || 'N/A'}</span>
            </Badge>
            <Badge tooltip={t('Budget')}>
              <PiMoneyLight className="text-gray-500 dark:text-gray-400" />
              <span>${lead.budget || 'N/A'}</span>
            </Badge>
            <Badge tooltip={t('Timeframe')}>
              <PiClockLight className="text-gray-500 dark:text-gray-400" />
              <span>{lead.timeframe || 'N/A'}</span>
            </Badge>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <PiCalendarLight className="text-gray-500 dark:text-gray-400" />
            <span className="font-medium">{t('Submitted at')}:</span>
            <span>{new Date(lead.created_at).toLocaleString()}</span>
          </div>
        </div>
        <FilterBarWithTabs tabs={tabs} activeTab={activeTab} onTabClick={goToTab} />
        <div className="mt-2 px-4">
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'orientation' && renderOrientationTab()}
          {activeTab === 'offer' && renderOfferTab()}
          {activeTab === 'engagement' && renderEngagementTermsTab()}
          {activeTab === 'billing' && BillingPage()}
        </div>
      </div>
    </DetailFrame>
  );
};

const OfferCard: React.FC<{ offer: OfferPayload; status: string }> = ({ offer, status }) => {
  const { t } = useTranslation();
  const statusColors: Record<string, BadgeColor> = {
    Offered: 'blue',
    Accepted: 'green',
    Declined: 'red',
  };

  return (
    <div className="glass hover:bg-gray-50 transition rounded-lg p-6 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400 h-6 w-6" />
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">{t('Offer details')}</h2>
        </div>
        <Badge color={statusColors[status] || 'gray'} className="text-sm">{t(status)}</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300">
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Total price')}:</span>
          <span>${offer.totalPrice}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiClockLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Planned hours')}:</span>
          <span>{offer.plannedHours || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiCurrencyDollarLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Price per hour')}:</span>
          <span>${offer.pricePerHour || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2">
          <PiClockLight className="text-gray-500 dark:text-gray-400" />
          <span className="font-medium">{t('Timeframe')}:</span>
          <span>{offer.timeframe}</span>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Scope of work')}:</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.scopeOfWork}</p>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Expected outcome')}:</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.expectedOutcome}</p>
        </div>
      </div>
      <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
        <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
        <div className="flex-1">
          <span className="font-medium block">{t('Communication plan')}:</span>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.communicationPlan || 'N/A'}</p>
        </div>
      </div>
      <div className="mt-4">
        <span className="font-medium text-gray-700 dark:text-gray-300">{t('Payment terms')}:</span>
        <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mt-1">
          <li>{t('Retainer')}: ${offer.paymentTerms.retainer || 'N/A'}</li>
          <li>{t('Intervals')}: {offer.paymentTerms.intervals || 'N/A'}</li>
          <li>{t('Methods')}: {offer.paymentTerms.methods.join(', ')}</li>
        </ul>
      </div>
      {offer.expertise && (
        <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
          <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
          <div className="flex-1">
            <span className="font-medium block">{t('Expertise')}:</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.expertise}</p>
          </div>
        </div>
      )}
      {offer.risks && (
        <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
          <PiChatCircleTextLight className="text-gray-500 dark:text-gray-400 mt-1" />
          <div className="flex-1">
            <span className="font-medium block">{t('Risks and challenges')}:</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{offer.risks}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const OfferForm: React.FC<{ offer: OfferPayload; setOffer: (offer: OfferPayload) => void; onSubmit: () => void; onCancel?: () => void }> = ({ offer, setOffer, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-lg font-medium mb-4">{t('Pricing')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={t('Total price') + ' *'}
            type="number"
            placeholder={t('Enter total estimated cost')}
            value={offer.totalPrice}
            onChange={(e) => setOffer({ ...offer, totalPrice: parseFloat(e.target.value) || 0 })}
            className="w-full"
            id="totalPrice"
          />
          <Input
            label={t('Planned hours')}
            type="number"
            placeholder={t('Enter estimated hours')}
            value={offer.plannedHours || ''}
            onChange={(e) => setOffer({ ...offer, plannedHours: parseFloat(e.target.value) || undefined })}
            className="w-full"
            id="plannedHours"
          />
          <Input
            label={t('Price per hour')}
            type="number"
            placeholder={t('Enter hourly rate')}
            value={offer.pricePerHour || ''}
            onChange={(e) => setOffer({ ...offer, pricePerHour: parseFloat(e.target.value) || undefined })}
            className="w-full"
            id="pricePerHour"
          />
        </div>
      </section>
      <section>
        <h3 className="text-lg font-medium mb-4">{t('Project details')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            label={t('Timeframe') + ' *'}
            type="text"
            placeholder={t('e.g., 3 weeks')}
            value={offer.timeframe}
            onChange={(e) => setOffer({ ...offer, timeframe: e.target.value })}
            className="w-full"
            id="timeframe"
          />
          <Input
            label={t('Communication plan')}
            type="text"
            placeholder={t('e.g., weekly emails')}
            value={offer.communicationPlan || ''}
            onChange={(e) => setOffer({ ...offer, communicationPlan: e.target.value })}
            className="w-full"
            id="communicationPlan"
          />
        </div>
        <Textarea
          label={t('Expected outcome') + ' *'}
          placeholder={t('Describe possible scenarios')}
          value={offer.expectedOutcome}
          onChange={(e) => setOffer({ ...offer, expectedOutcome: e.target.value })}
          className="w-full"
          rows={4}
          id="expectedOutcome"
        />
      </section>
      <section>
        <h3 className="text-lg font-medium mb-4">{t('Payment terms')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={t('Retainer')}
            type="number"
            placeholder={t('Enter retainer amount')}
            value={offer.paymentTerms.retainer || ''}
            onChange={(e) => setOffer({ ...offer, paymentTerms: { ...offer.paymentTerms, retainer: parseFloat(e.target.value) || undefined } })}
            className="w-full"
            id="retainer"
          />
          <Input
            label={t('Payment intervals')}
            type="text"
            placeholder={t('e.g., every 1.5 weeks')}
            value={offer.paymentTerms.intervals || ''}
            onChange={(e) => setOffer({ ...offer, paymentTerms: { ...offer.paymentTerms, intervals: e.target.value } })}
            className="w-full"
            id="intervals"
          />
          <Input
            label={t('Accepted payment methods') + ' *'}
            type="text"
            value="PONS"
            className="w-full bg-gray-200 dark:bg-gray-700 cursor-not-allowed"
            id="paymentMethods"
          />
        </div>
      </section>
      <section className="space-y-3">
        <Textarea
          label={t('Scope of work') + ' *'}
          placeholder={t('Describe services included')}
          value={offer.scopeOfWork}
          onChange={(e) => setOffer({ ...offer, scopeOfWork: e.target.value })}
          className="w-full"
          rows={4}
          id="scopeOfWork"
        />
        <Textarea
          label={t('Expertise')}
          placeholder={t('Highlight relevant experience')}
          value={offer.expertise || ''}
          onChange={(e) => setOffer({ ...offer, expertise: e.target.value })}
          className="w-full mt-4"
          rows={4}
          id="expertise"
        />
        <Textarea
          label={t('Risks and challenges')}
          placeholder={t('Describe potential obstacles')}
          value={offer.risks || ''}
          onChange={(e) => setOffer({ ...offer, risks: e.target.value })}
          className="w-full mt-4"
          rows={4}
          id="risks"
        />
      </section>
      <div className="flex justify-end gap-4">
        {onCancel && (
          <Button onClick={onCancel} variant="neutral" size="small">
            {t('Cancel')}
          </Button>
        )}
        <Button onClick={onSubmit} variant="secondary" size="small">
          {t('Submit offer')}
        </Button>
      </div>
    </div>
  );
};

export default LeadDetailsPage;
